"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = void 0;
var _styles = require("@material-ui/core/styles");
var _propTypes = _interopRequireDefault(require("prop-types"));
var _react = _interopRequireDefault(require("react"));
var _SeatReservationContainer = _interopRequireDefault(require("./SeatReservationContainer.js"));
var _SeatReservationSpace = _interopRequireDefault(require("./SeatReservationSpace.js"));
var _EmptyStateGate = _interopRequireDefault(require("../Components/EmptyStateGate.js"));
var _material = require("@heron-web/material");
var _SeatReservationAssetModule = _interopRequireDefault(require("./SeatReservationAsset.module.scss"));
var _jsxRuntime = require("react/jsx-runtime");
function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { default: obj }; }
/**
 * An asset (carriage) in the seat reservation UI
 * 
 * @package HOPS
 * @subpackage SeatReservation
 * @author Heron Web Ltd
 * @copyright Heritage Operations Processing Limited
 */
const SeatReservationAsset = props => {
  /**
   * Render a space.
   *
   * @param {Object} space
   * @return {ReactNode}
   */
  const renderSpace = space => {
    const seatReservation = props.seatReservations.find(_ref => {
      let {
        TrainAsset,
        Space
      } = _ref;
      return TrainAsset === props.asset.Id && Space === space.Id;
    });
    const booked = !!seatReservation;
    const excluded = space?.Container?.Exclusive &&
    // Container must want to be exclusive
    !!props.seatReservations.filter(x => x.Order !== props.targetOrderId && x.Basket !== props.targetBasketId) // Only check seats that aren't in our order/basket
    .find(_ref2 => {
      let {
        TrainAsset,
        Container
      } = _ref2;
      return TrainAsset === props.asset.Id && Container === space?.Container?.Id;
    });
    const bookedWithinOrder = seatReservation ? props.targetBasketId && seatReservation.Basket === props.targetBasketId || props.targetOrderId && seatReservation.Order === props.targetOrderId : false;
    return /*#__PURE__*/(0, _jsxRuntime.jsx)(_SeatReservationSpace.default, {
      booked: booked,
      bookedWithinOrder: bookedWithinOrder,
      excluded: excluded,
      isInContainer: !!props.containerLevel && isSpaceInRenderedContainer(space),
      onToggleSelection: handleSpaceSelectionToggle,
      selectable: !props.maxSelectionExceeded,
      selected: props.activeSpaceSelection.includes(space.Id),
      space: space
    }, space.Id);
  };

  /**
   * Space selection toggled.
   *
   * @param {Object} space Seat plan space object
   * @return {void}
   */
  const handleSpaceSelectionToggle = space => {
    const train = {
      ...props.train
    };
    delete train.Assets;
    delete train.TimingPoints;
    const trainAsset = {
      ...props.asset
    };
    props.onChangeSelection(train, trainAsset, props.trainScheduleIdDeparture, props.trainScheduleIdArrival, space);
  };

  /**
   * Get whether a space is within a rendered container.
   *
   * @param {Object} space
   * @return {Boolean}
   */
  const isSpaceInRenderedContainer = space => {
    return !!containers.find(_ref3 => {
      let {
        X1,
        X2,
        Y1,
        Y2
      } = _ref3;
      return X1 <= space.X && X2 >= space.X && Y1 <= space.Y && Y2 >= space.Y;
    });
  };

  /**
   * Get a container object's parent at a given level.
   *
   * @param {Object} container
   * @param {Integer} level
   * @return {Object|null}
   */
  const resolveContainerParentLevel = (container, level) => {
    let at = 0;
    while (at < level) {
      container = container?.Parent;
      if (!container) break;else at++;
    }
    return container;
  };

  /**
   * Get container space definitions to render.
   *
   * Containers are defined independently of spaces. They're simple
   * entities which don't include any positional information. Spaces
   * represent a single cell in the grid. Therefore we compute the
   * boundaries of the container by working out the extent of the
   * spaces that exist within it (at the given level).
   * 
   * @return {Array}
   */
  const getContainers = () => {
    const containers = {};
    props.seatPlan?.Spaces?.forEach(_ref4 => {
      let {
        Container,
        X,
        Y
      } = _ref4;
      Container = resolveContainerParentLevel(Container, props.containerLevel - 1);
      if (Container) {
        /**
         * Never seen this container before
         *
         * Initialise its coordinates to the space's for now.
         */
        if (!containers[Container.Id]) {
          containers[Container.Id] = {
            Container,
            X1: X,
            X2: X,
            Y1: Y,
            Y2: Y
          };
        }

        /**
         * This space is within a previously seen container
         *
         * If it lies outside the previously known bounds of
         * the container, we need to adjust those bounds now.
         */else {
          if (X < containers[Container.Id].X1) {
            containers[Container.Id].X1 = X;
          }
          if (X > containers[Container.Id].X2) {
            containers[Container.Id].X2 = X;
          }
          if (Y < containers[Container.Id].Y1) {
            containers[Container.Id].Y1 = Y;
          }
          if (Y > containers[Container.Id].Y2) {
            containers[Container.Id].Y2 = Y;
          }
        }
      }
    });
    return Object.values(containers);
  };
  const containers = getContainers();
  const styles = {
    "--hops-seat-reservation-asset-background-colour": props.theme.palette.background.default,
    "--hops-seat-reservation-asset-border-colour": props.theme.palette.text.disabled?.replace("rgba(", "")?.replace(")", "")?.split(", ")?.splice(0, 3)?.join(", ")
  };
  return /*#__PURE__*/(0, _jsxRuntime.jsx)("div", {
    className: _SeatReservationAssetModule.default.root,
    style: styles,
    children: /*#__PURE__*/(0, _jsxRuntime.jsxs)(_material.Flex, {
      gap: 0.25,
      children: [/*#__PURE__*/(0, _jsxRuntime.jsxs)("div", {
        className: _SeatReservationAssetModule.default.fourFoot,
        children: [/*#__PURE__*/(0, _jsxRuntime.jsx)("div", {
          className: `${_SeatReservationAssetModule.default.rail} ${_SeatReservationAssetModule.default.railTop}`
        }), /*#__PURE__*/(0, _jsxRuntime.jsx)("div", {
          className: `${_SeatReservationAssetModule.default.rail} ${_SeatReservationAssetModule.default.railBottom}`
        }), /*#__PURE__*/(0, _jsxRuntime.jsxs)("div", {
          className: _SeatReservationAssetModule.default.asset,
          children: [/*#__PURE__*/(0, _jsxRuntime.jsx)("div", {
            className: `${_SeatReservationAssetModule.default.buffer} ${_SeatReservationAssetModule.default.bufferTopLeft}`
          }), /*#__PURE__*/(0, _jsxRuntime.jsx)("div", {
            className: `${_SeatReservationAssetModule.default.buffer} ${_SeatReservationAssetModule.default.bufferTopRight}`
          }), /*#__PURE__*/(0, _jsxRuntime.jsx)("div", {
            className: `${_SeatReservationAssetModule.default.buffer} ${_SeatReservationAssetModule.default.bufferBottomLeft}`
          }), /*#__PURE__*/(0, _jsxRuntime.jsx)("div", {
            className: `${_SeatReservationAssetModule.default.buffer} ${_SeatReservationAssetModule.default.bufferBottomRight}`
          }), /*#__PURE__*/(0, _jsxRuntime.jsx)(_EmptyStateGate.default, {
            isEmpty: !props.seatPlan?.Spaces?.length,
            children: /*#__PURE__*/(0, _jsxRuntime.jsxs)("div", {
              className: _SeatReservationAssetModule.default.seatPlan,
              children: [!!props.containerLevel && containers.map(c => /*#__PURE__*/(0, _jsxRuntime.jsx)(_SeatReservationContainer.default, {
                def: c
              }, c.Container.Id)), props.seatPlan?.Spaces?.map(renderSpace)]
            })
          })]
        })]
      }), /*#__PURE__*/(0, _jsxRuntime.jsxs)(_material.Flex, {
        gap: 0,
        children: [/*#__PURE__*/(0, _jsxRuntime.jsx)(_material.String, {
          color: "textSecondary",
          str: props.assetLabel
        }), /*#__PURE__*/(0, _jsxRuntime.jsx)(_material.String, {
          color: "textSecondary",
          str: props.assetRemarks,
          variant: "caption"
        })]
      })]
    })
  });
};
SeatReservationAsset.propTypes = {
  activeSpaceSelection: _propTypes.default.array,
  containerLevel: _propTypes.default.number,
  maxSelectionExceeded: _propTypes.default.bool,
  onChangeActiveSelection: _propTypes.default.func,
  seatReservations: _propTypes.default.array,
  seatPlan: _propTypes.default.object
};
SeatReservationAsset.defaultProps = {
  activeSpaceSelection: [],
  containerLevel: 0,
  maxSelectionExceeded: false,
  seatReservations: []
};
var _default = (0, _styles.withTheme)(SeatReservationAsset);
exports.default = _default;